import React, { useState } from 'react'
import PaginationComp from 'components/Pagination'
import TableComp from 'components/Table'
import useSosCDR from 'hooks/useSosCDR'
import { faCircleNotch, faDownload, faFileExcel, faPlay, faStop } from '@fortawesome/free-solid-svg-icons'
import CustomTitle from 'components/CustomTitle'
import ButtonIcon from 'components/ButtonComp'
import useLoading from 'hooks/useLoading'
import * as XLSX from 'xlsx'
import { ISosCdr } from 'models/Cdr'
import Filters from './Filters'
import useForm from 'hooks/useForm'
import { message } from 'antd'
import moment from 'moment'

const CDR: React.FC = () => {
    const hookForm = useForm({ disposition: 'all', src_sos: 'all' });
    const { src_sos, disposition } = hookForm.form;
    const hookCdr = useSosCDR({ assetId: src_sos === 'all' ? undefined : parseInt(src_sos), disposition: disposition === 'all' ? undefined : disposition, page: 1, pageSize: 20 });
    const hookCdrXlsx = useSosCDR({ assetId: src_sos === 'all' ? undefined : parseInt(src_sos), disposition: disposition === 'all' ? undefined : disposition });
    const hookLoadingXlsx = useLoading({ defaultLoading: false });
    const [activeAudio, setActiveAudio] = useState<{ id: number, status: 'loading' | 'playing' } | null>(null);

    const handlePlayRecording = (item: ISosCdr) => {
        if (item.recordingfile) {
            if (activeAudio) {
                message.destroy(`audio-player-${activeAudio.id}`);
            }
            const key = `audio-player-${item.id}`;
            message.loading({ content: 'Cargando audio...', key, duration: 0 });
            setActiveAudio({ id: item.id, status: 'loading' });
            const startTime = performance.now();
            const baseUrl = `${process.env.REACT_APP_LOCAL_SERVER}/issabel/recordings/`;
            const audioUrl = `${baseUrl}${item.recordingfile}`;

            const audio = new Audio(audioUrl);
            audio.oncanplay = () => {
                const loadTime = performance.now() - startTime;
                const loadTimeInSeconds = loadTime / 1000;
                console.log(`El audio tardó ${loadTime.toFixed(2)} ms en cargar.`);
                setActiveAudio({ id: item.id, status: 'playing' });
                message.open({
                    key,
                    content: (
                        <div className='d-flex align-items-center gap-3'>
                            {item.src_sos}<br />
                            {moment(item.calldate).format('lll')}
                            <audio controls autoPlay src={audioUrl} key={audioUrl}>
                                <source src={audioUrl} type="audio/wav" />
                                Tu navegador no soporta el elemento de audio.
                            </audio>
                        </div>
                    ),
                    duration: item.duration + loadTimeInSeconds,
                    onClose: () => {
                        if (activeAudio?.id === item.id) {
                            setActiveAudio(null);
                        }
                    }
                });
            }
            audio.onerror = () => {
                message.error({ content: 'Error al cargar el audio.', key });
                setActiveAudio(null);
            }

        } else {
            message.error('No hay grabación disponible para esta llamada.');
        }
    };

    const handleDownloadXLSX = () => {
        hookLoadingXlsx.start();
        try {
            const exportData = hookCdrXlsx.data.map((item: ISosCdr) => ({
                'ID': item.id,
                'Fecha': item.calldate,
                'SOS': item.src_sos,
                'Origen': item.src,
                'Destino': item.dst,
                'Canal Origen': item.channel,
                'Canal Destino': item.dstchannel,
                'Estado': item.disposition,
                'Duración': item.duration,
                'UniqueID': item.uniqueid,
                'Grabación': item.recordingfile,
                'Número de Cliente': item.cnum,
                'Nombre de Cliente': item.cnam,
                'Nombre de Cliente Saliente': item.outbound_cnam,
                'Nombre de Destino': item.dst_cnam,
                'ID Vinculado': item.linkedid,
                'Cuenta de Peers': item.peeraccount,
                'Secuencia': item.sequence
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Registro Detallado de Llamadas');

            // Generate filename with timestamp
            const fileName = `sos_cdr_${new Date().getTime()}.xlsx`;

            // Save the file
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error('Error al exportar a XLSX:', error);
        } finally {
            hookLoadingXlsx.end();
        }
    }

    const handleDownloadAudio = async(item: ISosCdr) => {
        if (item.recordingfile) {
            const key = `download-audio-${item.id}`;
            message.loading({ content: 'Descargando audio...', key, duration: 0 });

            try {
                const baseUrl = `${process.env.REACT_APP_LOCAL_SERVER}/issabel/recordings/`;
                const audioUrl = `${baseUrl}${item.recordingfile}`;

                const response = await fetch(audioUrl);

                if (!response.ok) {
                    throw new Error('La respuesta de la red no fue correcta.');
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', item.recordingfile);
                document.body.appendChild(link);
                link.click();

                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                message.success({ content: 'Audio descargado.', key });

            } catch (error) {
                console.error('Error al descargar el audio:', error);
                message.error({ content: 'Error al descargar el audio.', key });
            }
        } else {
            message.error('No hay grabación disponible para descargar.');
        }
    }

    const getActionIcon = (item: ISosCdr) => {
        const audioState = activeAudio?.id === item.id ? activeAudio.status : null;
        if (audioState === 'loading') return { icon: faCircleNotch, spin: true, tooltip: "Cargando..." };
        if (audioState === 'playing') return { icon: faStop, spin: false, tooltip: "Detener" };
        return { icon: faPlay, spin: false, tooltip: "Escuchar" };
    };

    return (<>
        <CustomTitle
            className="mb-2"
            actions={[
                <ButtonIcon key="download" name="Descargar" icon={faFileExcel} variant="success" onClick={handleDownloadXLSX} disabled={hookLoadingXlsx.isLoading || hookCdrXlsx.loading} />,
                <Filters hookForm={hookForm} />
            ]}
        />

        <TableComp
            headers={['#', 'Fecha', 'SOS', 'Origen', 'Destino', 'Canal Origen', 'Canal Destino', 'Estado', 'Duración', 'UniqueID', 'Acciones']}
            keys={['#', 'calldate', 'src_sos', 'src', 'dst', 'channel', 'dstchannel', 'disposition', 'duration', 'uniqueid']}
            body={hookCdr.data}
            loading={hookCdr.loading}
            loadingItems={hookCdr.pageSize}
            page={hookCdr.page}
            actions={(item) => {
                const { icon, spin, tooltip } = getActionIcon(item);
                return [
                    {
                        icon: faDownload,
                        tooltip: "Descargar",
                        handleClick: handleDownloadAudio,
                        variant: "outline-success",
                        showWhenNotNull: ['recordingfile'],
                        dinamic: [{ key: 'disposition', value: 'ANSWERED' }]
                    }, {
                        icon,
                        spin,
                        tooltip,
                        handleClick: handlePlayRecording,
                        variant: "success",
                        disabled: !!activeAudio && activeAudio.id === item.id,
                        dinamic: [{ key: 'disposition', value: 'ANSWERED' }]
                    },
                ]
            }}
            moment_dates={[{ index: 1, format: 'lll' }]}
            seconds={[{ index: 8, format: 'HH:mm:ss' }]}
            badges={[{ index: 7 }]}
            badges_values={[
                {
                    col: 'disposition',
                    values: [
                        { value: 'ANSWERED', name: 'CONTESTADO', color: '#008000' },
                        { value: 'CONGESTION', name: 'CONGESTIÓN', color: '#008080' },
                        { value: 'NO ANSWER', name: 'NO CONTESTADO', color: '#e67e22' },
                        { value: 'FAILED', name: 'FAILED', color: '#C70039' },
                    ]
                }
            ]}

        />
        <PaginationComp hookPagination={hookCdr} />
    </>)
}

export default CDR